"use client";

import { useEffect, useId } from "react";

import {
  INSEE_LOCKED_FIELD_HINT,
  PartyProfileFields,
  ProfileFieldLabel,
  ProfileTextInput,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/party-profile-fields";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldContent, FieldError } from "@/components/ui/field";
import type { EditableCompanyProfile } from "@/lib/cat/company-profile.types";
import { isCompanyContactEditComplete } from "@/lib/cat/party-profile-validation";
import { validateCompanyProfileFields } from "@/lib/cat/party-profile-validation";

function CompanyContactEditForm({
  value,
  onChange,
  onCanProceedChange,
  title = "Coordonnées RefPers",
  description = "Vous pouvez modifier l'email et les numéros de téléphone. Les autres informations proviennent du Référentiel Personne.",
}: {
  value: EditableCompanyProfile;
  onChange: (value: EditableCompanyProfile) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
  title?: string;
  description?: string;
}) {
  const idPrefix = useId();
  const canProceed = isCompanyContactEditComplete(value);
  const identityErrors = validateCompanyProfileFields(value);
  const contactFieldErrors = {
    email: identityErrors.email,
    fixedPhone: identityErrors.fixedPhone,
    mobilePhone: identityErrors.mobilePhone,
  };

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <Card className="border-muted">
      <CardContent className="space-y-4 pt-6">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-muted-foreground text-sm">{description}</p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field>
            <ProfileFieldLabel
              htmlFor={`${idPrefix}-denomination`}
              label="Raison sociale"
              locked
              lockedHint={INSEE_LOCKED_FIELD_HINT}
            />
            <FieldContent>
              <ProfileTextInput
                id={`${idPrefix}-denomination`}
                locked
                value={value.companyDenomination}
                onChange={() => undefined}
              />
            </FieldContent>
          </Field>
          <Field>
            <ProfileFieldLabel
              htmlFor={`${idPrefix}-ape`}
              label="Code APE"
              locked
              lockedHint={INSEE_LOCKED_FIELD_HINT}
            />
            <FieldContent>
              <ProfileTextInput
                id={`${idPrefix}-ape`}
                locked
                value={value.apeCode}
                onChange={() => undefined}
              />
            </FieldContent>
          </Field>
        </div>

        <PartyProfileFields
          idPrefix={`${idPrefix}-party`}
          value={value}
          onChange={(party) => onChange({ ...value, ...party })}
          lockedFields={{
            addressLine: true,
            addressFloorApartment: true,
            addressBuildingResidence: true,
            postBox: true,
            postalCode: true,
            city: true,
            email: false,
            fixedPhone: false,
            mobilePhone: false,
          }}
          lockedFieldHint={INSEE_LOCKED_FIELD_HINT}
          showValidation
          requireEmail={false}
        />

        {contactFieldErrors.email ? (
          <FieldError>{contactFieldErrors.email}</FieldError>
        ) : null}
      </CardContent>
    </Card>
  );
}

export { CompanyContactEditForm };
